import { useState, useEffect } from 'react';
import {
  SITE_COPY_KEYS,
  WELLNESS_PLACEMENT_KEYS,
  WELLNESS_PLACEMENT_LABELS,
  type SiteCopyKey,
  type WellnessPlacementKey,
  type WellnessSiteManageContent,
  type WellnessSlideRecord,
} from '@lighthouse/shared';
import { adminApi } from '../services/api';
import { ConfirmDialog } from '../components/ConfirmDialog';

type Tab = 'slides' | 'placements' | 'copy';

interface SlideForm {
  key: string;
  caption: string;
  imageUrl: string;
  gradient1: string;
  gradient2: string;
  gradient3: string;
  isActive: boolean;
  libraryOrder: string;
}

const emptySlide = (): SlideForm => ({
  key: '',
  caption: '',
  imageUrl: '',
  gradient1: '#1A4A58',
  gradient2: '#215667',
  gradient3: '#7EC4CB',
  isActive: true,
  libraryOrder: '0',
});

function slideToForm(slide: WellnessSlideRecord): SlideForm {
  const [gradient1, gradient2, gradient3] = slide.gradient;
  return {
    key: slide.key,
    caption: slide.caption,
    imageUrl: slide.imageUrl,
    gradient1,
    gradient2,
    gradient3,
    isActive: slide.isActive,
    libraryOrder: String(slide.libraryOrder),
  };
}

function formToSlidePayload(form: SlideForm) {
  const libraryOrder = Number.parseInt(form.libraryOrder, 10);
  return {
    key: form.key,
    caption: form.caption,
    imageUrl: form.imageUrl,
    gradient: [form.gradient1, form.gradient2, form.gradient3] as [string, string, string],
    isActive: form.isActive,
    libraryOrder: Number.isFinite(libraryOrder) ? libraryOrder : 0,
  };
}

export function WellnessPage() {
  const [tab, setTab] = useState<Tab>('slides');
  const [content, setContent] = useState<WellnessSiteManageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteSlideId, setDeleteSlideId] = useState<string | null>(null);

  const [editingSlideId, setEditingSlideId] = useState<string | null>(null);
  const [slideForm, setSlideForm] = useState<SlideForm>(emptySlide());

  const [selectedPlacement, setSelectedPlacement] = useState<WellnessPlacementKey>('landing');
  const [placementSlideIds, setPlacementSlideIds] = useState<string[]>([]);

  const [copyDraft, setCopyDraft] = useState<Record<SiteCopyKey, string>>(
    {} as Record<SiteCopyKey, string>
  );

  const load = () => {
    setLoading(true);
    adminApi<WellnessSiteManageContent>('/site/wellness/manage')
      .then((data) => {
        setContent(data);
        const copyMap = {} as Record<SiteCopyKey, string>;
        for (const row of data.copy) {
          copyMap[row.key] = row.value;
        }
        setCopyDraft(copyMap);
        const placement = data.placements.find((p) => p.key === selectedPlacement);
        setPlacementSlideIds(placement?.slideIds ?? []);
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!content) return;
    const placement = content.placements.find((p) => p.key === selectedPlacement);
    setPlacementSlideIds(placement?.slideIds ?? []);
  }, [selectedPlacement, content]);

  const handleSaveSlide = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = formToSlidePayload(slideForm);
      if (editingSlideId === 'new') {
        await adminApi('/site/wellness/slides', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else if (editingSlideId) {
        await adminApi(`/site/wellness/slides/${editingSlideId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }
      setEditingSlideId(null);
      setSlideForm(emptySlide());
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSlide = (id: string) => {
    setDeleteSlideId(id);
  };

  const handleSavePlacement = async () => {
    setSaving(true);
    setError('');
    try {
      await adminApi(`/site/wellness/placements/${selectedPlacement}`, {
        method: 'PUT',
        body: JSON.stringify({ slideIds: placementSlideIds }),
      });
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCopy = async () => {
    setSaving(true);
    setError('');
    try {
      const updates = SITE_COPY_KEYS.map((key) => ({
        key,
        value: copyDraft[key] ?? '',
      }));
      const data = await adminApi<WellnessSiteManageContent>('/site/wellness/copy', {
        method: 'PUT',
        body: JSON.stringify({ updates }),
      });
      setContent(data);
      const copyMap = {} as Record<SiteCopyKey, string>;
      for (const row of data.copy) {
        copyMap[row.key] = row.value;
      }
      setCopyDraft(copyMap);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const togglePlacementSlide = (slideId: string) => {
    setPlacementSlideIds((current) =>
      current.includes(slideId) ? current.filter((id) => id !== slideId) : [...current, slideId]
    );
  };

  const movePlacementSlide = (index: number, direction: -1 | 1) => {
    setPlacementSlideIds((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      const [item] = next.splice(index, 1);
      if (!item) return current;
      next.splice(target, 0, item);
      return next;
    });
  };

  const activeSlides = content?.slides.filter((s) => s.isActive) ?? [];

  if (editingSlideId) {
    return (
      <div className="max-w-2xl">
        <div className="mb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={() => {
              setEditingSlideId(null);
              setSlideForm(emptySlide());
            }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900">
            {editingSlideId === 'new' ? 'New slide' : 'Edit slide'}
          </h1>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Key (unique id)</label>
            <input
              className="input-field mt-1 w-full font-mono text-sm"
              value={slideForm.key}
              onChange={(e) => setSlideForm((p) => ({ ...p, key: e.target.value }))}
              disabled={editingSlideId !== 'new'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Caption</label>
            <input
              className="input-field mt-1 w-full"
              value={slideForm.caption}
              onChange={(e) => setSlideForm((p) => ({ ...p, caption: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Image URL</label>
            <input
              className="input-field mt-1 w-full"
              value={slideForm.imageUrl}
              onChange={(e) => setSlideForm((p) => ({ ...p, imageUrl: e.target.value }))}
              placeholder="https://..."
            />
            {slideForm.imageUrl ? (
              <img
                src={slideForm.imageUrl}
                alt=""
                className="mt-2 h-32 w-full rounded-xl object-cover"
              />
            ) : null}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(['gradient1', 'gradient2', 'gradient3'] as const).map((field, i) => (
              <div key={field}>
                <label className="block text-xs font-medium text-gray-700">Gradient {i + 1}</label>
                <input
                  type="color"
                  className="mt-1 h-10 w-full cursor-pointer rounded-lg border border-gray-200"
                  value={slideForm[field]}
                  onChange={(e) => setSlideForm((p) => ({ ...p, [field]: e.target.value }))}
                />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Library order</label>
              <input
                type="number"
                min={0}
                className="input-field mt-1 w-full"
                value={slideForm.libraryOrder}
                onChange={(e) => setSlideForm((p) => ({ ...p, libraryOrder: e.target.value }))}
              />
            </div>
            <label className="mt-6 flex items-center gap-2">
              <input
                type="checkbox"
                checked={slideForm.isActive}
                onChange={(e) => setSlideForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              <span className="text-sm font-medium text-gray-800">Active</span>
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSaveSlide}
              disabled={saving}
              className="btn-primary px-6"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Wellness &amp; Site Content</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage carousel slides, where they appear, and hero headlines across web and mobile.
        </p>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="mb-6 flex gap-2">
        {(
          [
            ['slides', 'Slide library'],
            ['placements', 'Carousel placements'],
            ['copy', 'Hero copy'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${
              tab === id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">Loading…</div>
      ) : tab === 'slides' ? (
        <div>
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => {
                setEditingSlideId('new');
                setSlideForm(emptySlide());
              }}
            >
              + New slide
            </button>
          </div>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-200 text-xs font-semibold uppercase text-gray-500">
              <tr>
                <th className="py-2 pr-4">Preview</th>
                <th className="py-2 pr-4">Caption</th>
                <th className="py-2 pr-4">Key</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(content?.slides ?? []).map((slide) => (
                <tr key={slide.id} className="hover:bg-gray-50">
                  <td className="py-3 pr-4">
                    <img
                      src={slide.imageUrl}
                      alt=""
                      className="h-12 w-20 rounded-lg object-cover"
                    />
                  </td>
                  <td className="py-3 pr-4 font-medium text-gray-900">{slide.caption}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-gray-500">{slide.key}</td>
                  <td className="py-3 pr-4">
                    {slide.isActive ? (
                      <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        Active
                      </span>
                    ) : (
                      <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                        Hidden
                      </span>
                    )}
                  </td>
                  <td className="py-3">
                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="text-primary-600 hover:underline"
                        onClick={() => {
                          setEditingSlideId(slide.id);
                          setSlideForm(slideToForm(slide));
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-500 hover:underline"
                        onClick={() => {
                          void handleDeleteSlide(slide.id);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : tab === 'placements' ? (
        <div className="max-w-2xl space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Carousel surface</label>
            <select
              className="input-field mt-1 w-full"
              value={selectedPlacement}
              onChange={(e) => setSelectedPlacement(e.target.value as WellnessPlacementKey)}
            >
              {WELLNESS_PLACEMENT_KEYS.map((key) => (
                <option key={key} value={key}>
                  {WELLNESS_PLACEMENT_LABELS[key]}
                </option>
              ))}
            </select>
          </div>

          <div className="card space-y-3">
            <p className="text-sm font-semibold text-gray-800">Selected slides (in order)</p>
            {placementSlideIds.length === 0 ? (
              <p className="text-sm text-gray-500">No slides selected for this carousel.</p>
            ) : (
              placementSlideIds.map((slideId, index) => {
                const slide = content?.slides.find((s) => s.id === slideId);
                if (!slide) return null;
                return (
                  <div
                    key={slideId}
                    className="flex items-center gap-3 rounded-xl border border-white/60 bg-white/50 p-3"
                  >
                    <img src={slide.imageUrl} alt="" className="h-10 w-16 rounded object-cover" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{slide.caption}</p>
                      <p className="text-xs text-gray-500">{slide.key}</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        onClick={() => movePlacementSlide(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
                        onClick={() => movePlacementSlide(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        onClick={() => togglePlacementSlide(slideId)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="card space-y-2">
            <p className="text-sm font-semibold text-gray-800">Add slides</p>
            {activeSlides.map((slide) => {
              const selected = placementSlideIds.includes(slide.id);
              return (
                <label
                  key={slide.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-white/50"
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => togglePlacementSlide(slide.id)}
                  />
                  <img src={slide.imageUrl} alt="" className="h-8 w-12 rounded object-cover" />
                  <span className="text-sm text-gray-800">{slide.caption}</span>
                </label>
              );
            })}
          </div>

          <button
            type="button"
            onClick={handleSavePlacement}
            disabled={saving}
            className="btn-primary px-6"
          >
            {saving ? 'Saving…' : 'Save placement'}
          </button>
        </div>
      ) : (
        <div className="max-w-2xl space-y-4">
          {(content?.copy ?? []).map((row) => (
            <div key={row.key}>
              <label className="block text-sm font-medium text-gray-700">{row.label}</label>
              <textarea
                className="input-field mt-1 w-full"
                rows={row.key.includes('subline') || row.key.includes('tagline') ? 2 : 2}
                value={copyDraft[row.key] ?? ''}
                onChange={(e) => setCopyDraft((prev) => ({ ...prev, [row.key]: e.target.value }))}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={handleSaveCopy}
            disabled={saving}
            className="btn-primary px-6"
          >
            {saving ? 'Saving…' : 'Save hero copy'}
          </button>
        </div>
      )}
      <ConfirmDialog
        open={deleteSlideId !== null}
        title="Delete slide?"
        message="Delete this slide? It will be removed from all carousels."
        confirmLabel="Delete"
        danger
        onCancel={() => setDeleteSlideId(null)}
        onConfirm={async () => {
          if (!deleteSlideId) return;
          await adminApi(`/site/wellness/slides/${deleteSlideId}`, { method: 'DELETE' });
          setDeleteSlideId(null);
          load();
        }}
      />
    </div>
  );
}
